import { useEffect, type ReactNode } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { AppRootWrapperBlueprint } from '@backstage/plugin-app-react';
import { lightTokens } from './theme/rafayTheme';

// Backstage's scaffolder Stepper (plugin-scaffolder-react's Stepper.esm.js)
// takes zero props for per-step icons, subtitles, or a step-count badge —
// it renders bare `MuiStepLabel` nodes with only `children: step.title`. No
// prop or slot to inject through. This restyles it via stable selectors
// instead of forking the component:
//  - `.MuiStep-root` / `.MuiStepIcon-root` are versioned, stable MUI v4
//    classnames (safe to target).
//  - `#root__title` / adjacent-sibling description are RJSF's own
//    deterministic ID-schema convention (`${idPrefix}__title`), not a
//    guessed hash — the one place this whole approach isn't fragile.
// Scoped to the wizard route only, same pattern as hideScaffolderTabs.tsx.

const STYLE_ID = 'rafay-scaffolder-wizard-styles';
const BODY_CLASS = 'rafay-scaffolder-wizard';
const BADGE_ID = 'rafay-step-badge';
const HERO_ID = 'rafay-wizard-hero';

const TEAL = lightTokens.teal;
const MINT = '#4FBEB0';
const BORDER = lightTokens.border;
const TEXT_SECONDARY = lightTokens.textSecondary;
const SUBTLE_SURFACE = lightTokens.surfaceAltBg;
const TEAL_SOFT_BG = lightTokens.tealSoftBg;

// This template has exactly 4 steps (3 named pages + the stepper's own
// auto-appended "Review"), in this fixed order — safe to key by position.
const STEP_META = [
  { subtitle: 'Tell us about your VM', icon: 'basic' },
  { subtitle: 'Configure network', icon: 'network' },
  { subtitle: 'Attach storage', icon: 'storage' },
  { subtitle: 'Review & confirm', icon: 'check' },
] as const;

const svgDataUri = (path: string) =>
  `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>${path}</svg>`,
  )}")`;

const ICONS: Record<(typeof STEP_META)[number]['icon'], string> = {
  basic: svgDataUri(
    "<rect x='4' y='3' width='16' height='18' rx='2'/><line x1='8' y1='8' x2='16' y2='8'/><line x1='8' y1='12' x2='16' y2='12'/><line x1='8' y1='16' x2='12' y2='16'/>",
  ),
  network: svgDataUri(
    "<circle cx='12' cy='5' r='2.5'/><circle cx='5' cy='19' r='2.5'/><circle cx='19' cy='19' r='2.5'/><line x1='12' y1='7.5' x2='6.5' y2='17'/><line x1='12' y1='7.5' x2='17.5' y2='17'/>",
  ),
  storage: svgDataUri(
    "<ellipse cx='12' cy='5' rx='8' ry='3'/><path d='M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5'/><path d='M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3'/>",
  ),
  check: svgDataUri("<polyline points='4 12 10 18 20 6'/>"),
};

const heroSvg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="140" height="140">
  <g opacity="0.9">
    <rect x="70" y="60" width="60" height="90" rx="6" fill="none" stroke="${TEAL}" stroke-width="2.5"/>
    <line x1="82" y1="78" x2="118" y2="78" stroke="${MINT}" stroke-width="2.5"/>
    <line x1="82" y1="94" x2="118" y2="94" stroke="${MINT}" stroke-width="2.5"/>
    <line x1="82" y1="110" x2="118" y2="110" stroke="${MINT}" stroke-width="2.5"/>
    <line x1="82" y1="126" x2="105" y2="126" stroke="${MINT}" stroke-width="2.5"/>
    <path d="M40 55 C40 40, 60 30, 75 38 C80 22, 105 20, 112 36 C130 32, 145 45, 138 62 C150 65, 150 82, 135 84 L45 84 C30 84, 28 60, 40 55 Z"
      fill="none" stroke="${TEAL}" stroke-width="2.5" opacity="0.55"/>
  </g>
</svg>`;

const WIZARD_CSS = `
body.${BODY_CLASS} .MuiStepper-root {
  background: ${TEAL_SOFT_BG};
  border: 1px solid ${BORDER};
  border-radius: 12px;
  padding: 24px 32px;
}

/* Per-step icon, overlaid on top of MUI's own numbered circle rather than
   fighting its internal SVG structure to remove the number. */
body.${BODY_CLASS} .MuiStep-root .MuiStepIcon-root {
  width: 36px;
  height: 36px;
  color: #C9C4B6;
}
body.${BODY_CLASS} .MuiStep-root .MuiStepIcon-root.MuiStepIcon-active,
body.${BODY_CLASS} .MuiStep-root .MuiStepIcon-root.MuiStepIcon-completed {
  color: ${TEAL};
}
body.${BODY_CLASS} .MuiStep-root .MuiStepIcon-text { display: none; }
/* The icon overlay has to live on StepLabel's <span> icon wrapper, not on
   StepIcon's own root — that root renders as an <svg> element (or, in the
   completed state, an entirely different CheckCircle <svg>), and CSS
   generated content (::after) does not reliably render directly on an SVG
   root in any browser. The wrapping <span class="MuiStepLabel-iconContainer">
   is a normal HTML element and doesn't have this problem. */
body.${BODY_CLASS} .MuiStepLabel-iconContainer {
  position: relative;
}
body.${BODY_CLASS} .MuiStepLabel-iconContainer::after {
  content: '';
  position: absolute;
  inset: 6px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  pointer-events: none;
}
${STEP_META.map(
  (step, i) => `
body.${BODY_CLASS} .MuiStep-root:nth-of-type(${
    i + 1
  }) .MuiStepLabel-iconContainer::after {
  background-image: ${ICONS[step.icon]};
}
body.${BODY_CLASS} .MuiStep-root:nth-of-type(${
    i + 1
  }) .MuiStepLabel-label::after {
  content: '${step.subtitle}';
  display: block;
  font-size: 0.75rem;
  font-weight: 400;
  color: ${TEXT_SECONDARY};
  margin-top: 2px;
}`,
).join('\n')}

/* Step-count badge, top-right of the page title row. Text content is kept
   in sync with the real active step via a tiny observer below (CSS alone
   can't read arbitrary state into content). */
#${BADGE_ID} {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${TEAL_SOFT_BG};
  border: 1px solid ${TEAL}59;
  color: ${TEAL};
  font-size: 0.75rem;
  font-weight: 600;
}

#${HERO_ID} {
  position: absolute;
  top: 8px;
  right: 24px;
  pointer-events: none;
  opacity: 0.9;
}
body.${BODY_CLASS} main [class*="BackstageHeader-header"] {
  position: relative;
}

/* Icon + boxed header above each step's field list, built from RJSF's own
   deterministic root title/description IDs (not a guessed hash). */
body.${BODY_CLASS} #root__title {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  margin: 0 0 4px !important;
  background: ${SUBTLE_SURFACE};
  border: 1px solid ${BORDER};
  border-radius: 10px 10px 0 0;
}
body.${BODY_CLASS} #root__title::before {
  content: '';
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex: 0 0 auto;
  background-color: ${TEAL};
  background-repeat: no-repeat;
  background-position: center;
  background-size: 18px;
}
/* The active step index is tracked in a data-attribute (set by the
   observer below) rather than guessed via CSS alone, so this icon always
   matches whichever step is actually showing instead of being stuck on
   step 1's icon regardless of page. */
${STEP_META.map(
  (step, i) => `
body.${BODY_CLASS}[data-wizard-step="${i}"] #root__title::before {
  background-image: ${ICONS[step.icon]};
}`,
).join('\n')}
body.${BODY_CLASS} #root__title .MuiDivider-root { display: none; }
body.${BODY_CLASS} #root__title + * {
  padding: 0 20px 16px;
  margin-top: 0 !important;
  border: 1px solid ${BORDER};
  border-top: none;
  border-radius: 0 0 10px 10px;
  background: ${lightTokens.surfaceBg};
}

/* Two-column field layout for each step's own top-level Grid. */
body.${BODY_CLASS} #root__title ~ .MuiGrid-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 32px;
}
body.${BODY_CLASS} #root__title ~ .MuiGrid-container > .MuiGrid-item {
  max-width: 100%;
  flex-basis: auto;
}
/* Multi-line fields (textarea) read awkwardly squeezed to half width. */
body.${BODY_CLASS} .MuiGrid-item:has(textarea) {
  grid-column: 1 / -1;
}

@media (prefers-reduced-motion: no-preference) {
  body.${BODY_CLASS} .MuiStep-root .MuiStepIcon-root,
  body.${BODY_CLASS} #root__title {
    transition: color 200ms ease, background-color 200ms ease;
  }
}
`;

function ensureStylesInjected() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = WIZARD_CSS;
  document.head.appendChild(style);
}

function ensureHeroInjected() {
  const header = document.querySelector('[class*="BackstageHeader-header"]');
  if (!header || document.getElementById(HERO_ID)) return;
  const hero = document.createElement('div');
  hero.id = HERO_ID;
  hero.innerHTML = heroSvg;
  header.appendChild(hero);
}

function ensureBadgeInjected() {
  const header = document.querySelector('[class*="BackstageHeader-header"]');
  if (!header || document.getElementById(BADGE_ID)) return;
  const badge = document.createElement('span');
  badge.id = BADGE_ID;
  header.appendChild(badge);
}

function updateWizardState() {
  const badge = document.getElementById(BADGE_ID);
  const steps = document.querySelectorAll('.MuiStepper-root .MuiStep-root');
  if (steps.length === 0) return;
  let activeIndex = 0;
  steps.forEach((step, i) => {
    if (step.querySelector('.MuiStepIcon-active, .Mui-active')) {
      activeIndex = i;
    }
  });
  if (badge) {
    badge.textContent = `Step ${activeIndex + 1} of ${steps.length}`;
  }
  // Drives #root__title's per-step icon (see WIZARD_CSS) — CSS alone can't
  // tell which step is active, so this is read from the data-attribute
  // instead of trying to guess it with a `:has()` chain.
  document.body.dataset.wizardStep = String(activeIndex);
}

function isWizardPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  return (
    segments[0] === 'create' &&
    segments[1] === 'templates' &&
    segments.length >= 4
  );
}

function ScaffolderWizardStyleWrapper({ children }: { children: ReactNode }) {
  useEffect(() => {
    ensureStylesInjected();

    const apply = () => {
      const onWizard = isWizardPath(window.location.pathname);
      document.body.classList.toggle(BODY_CLASS, onWizard);
      if (onWizard) {
        ensureHeroInjected();
        ensureBadgeInjected();
        updateWizardState();
      } else {
        delete document.body.dataset.wizardStep;
      }
    };

    apply();
    // Covers client-side route changes (no popstate) and step navigation
    // within the same route (activeStep is internal Stepper state, only
    // observable via its DOM effect — no popstate/location change fires).
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('popstate', apply);
    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', apply);
      document.body.classList.remove(BODY_CLASS);
      delete document.body.dataset.wizardStep;
    };
  }, []);

  return <>{children}</>;
}

export const scaffolderWizardStyleModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootWrapperBlueprint.make({
      name: 'scaffolder-wizard-style',
      params: { component: ScaffolderWizardStyleWrapper },
    }),
  ],
});
